<template>
  <!-- #MPC-8155: Vehicle workbench product tile implementation -->
  <!-- SEE TEMPLATE: https://dev-wb3.oneweb.mercedes-benz.com/developer/style-components/vehicle-tile#with-one-price -->
  <article class="wb-vehicle-tile emh-vehicle-tile">
    <div class="wb-vehicle-tile__top-bar">
      <div class="emh-vehicle-tile__badges-wrapper">
        <campaign-tags
          :badges="product.badges"
          :option="campaignBadgesVersion"
          @expand="onTagExpand"
          @modal-open="onModalOpen"
        />
      </div>
      <eqpodc-save-button
        v-if="showWishlistSaveButton"
        class="emh-vehicle-tile__save-button"
        :client-id.attr="'dcp-emh'"
        :commission-number.attr="product.commissionNumber"
        @saved="trackWishlistChanging('add_to_wishlist')"
        @deleted="trackWishlistChanging('remove_from_wishlist')"
      />
    </div>
    <emh-router-link
      class="emh-vehicle-tile__wrapper-top"
      :data-anchor="product.commissionNumber"
      :title="product.name"
      :to="{
        name: 'pdp',
        params: {
          code: product.code,
        },
      }"
      @click.prevent="trackLinkClickEvent"
    >
      <figure
        v-intersection-observer="{ threshold: 0, rootMargin: '0px 0px 200px 0px' }"
        class="wb-vehicle-tile__image"
        @viewport-entered="isInViewport = true"
      >
        <div v-if="!isInViewport" class="wb-vehicle-tile__image-img wb-skeleton"></div>
        <img
          v-else
          class="wb-vehicle-tile__image-img"
          :class="{ 'wb-vehicle-tile__image-img--fallback': hasImageLoadError || !hasValidImageUrl }"
          :src="vehicleImageUrl"
          :alt="product.name"
          @error="onImageLoadError"
        />
      </figure>
      <h2 class="wb-vehicle-tile__title">{{ product.name }}</h2>
    </emh-router-link>
    <ul class="wb-vehicle-tile__characteristics">
      <li
        v-if="product.powerTranslation"
        class="wb-vehicle-tile__characteristics-item wb-vehicle-tile__characteristics-item--emphasize"
      >
        <wb-icon :name.attr="'engine'"></wb-icon>
        {{ product.powerTranslation }}
        <footnote-reference
          v-if="showPowerFootnote"
          :text="$t('dcp.srp.footnotes.powerHybrid')"
          :reference="generateReference('powerHybrid')"
        />
      </li>
      <li v-if="product.fuelType" class="wb-vehicle-tile__characteristics-item">
        <wb-icon :name.attr="fuelTypeIcon"></wb-icon>
        {{ product.fuelType.name }}
      </li>
      <li v-if="showTransmission" class="wb-vehicle-tile__characteristics-item">
        <wb-icon :name.attr="'gear'"></wb-icon>
        {{ product.transmission }}
      </li>
      <li v-if="showSteeringWheelPosition && steeringWheelPosition" class="wb-vehicle-tile__characteristics-item">
        <wb-icon :name.attr="'bds/test-drive/24'" />
        {{ steeringWheelPosition }}
      </li>
    </ul>
    <stock-status
      v-if="product.stock"
      :stock-type="product.stock.stockType as StockType"
      :estimated-arrival-date="product.stock.estimatedArrivalDate"
      class="emh-vehicle-tile__stock-status -center"
    />
    <div class="wb-vehicle-tile__price-wrapper">
      <div class="wb-vehicle-tile__price" :class="{ '-reversed': displayReversedPrices }">
        <dh-price-display
          ref="priceDisplay"
          class="emh-vehicle-tile__price-display"
          :class="{ '-center': !showSummaryWidget || hasFinCalcError }"
          :loading.attr="'lazy'"
          :customer-type.attr="userContext"
          :post-code.attr="postCode"
          @pdinitialize="onPriceDisplayChange"
          @pdupdate="onPriceDisplayChange"
          @pdfootnotereferenceclick="scrollToFootnotes"
          @pdbreakdownopen="() => onPopoverOpen(PriceType.TotalPrice)"
        >
          <dh-price-display-label class="wb-vehicle-tile__price-headline" :type.attr="'purchase-price'">
          </dh-price-display-label>
          <div class="emh-vehicle-tile__purchase-price">
            <dh-price-display-value class="emh-vehicle-tile__purchase-price-value" :type.attr="'purchase-price'">
            </dh-price-display-value>
            <dh-price-display-price-breakdown class="emh-vehicle-tile__purchase-price-summary" />
          </div>
          <dh-price-display-value
            v-if="showStrikePrice"
            class="emh-vehicle-tile__strike-price-value"
            :type.attr="'strike-price'"
          >
          </dh-price-display-value>
        </dh-price-display>
      </div>

      <div v-if="showSummaryWidget" v-show="!hasFinCalcError" class="wb-vehicle-tile__price">
        <finance-summary
          :product="product"
          :lazy-load="true"
          @fcis-error="setFinCalcError(true)"
          @fcis-quote="setFinCalcError(false)"
          @loading="setFinCalcError(false)"
          @fcis-popup-open="() => onPopoverOpen(PriceType.MonthlyRate)"
        ></finance-summary>
      </div>
    </div>
    <dh-price-display
      ref="priceDisplayEco"
      class="emh-vehicle-tile__price-display-eco"
      :loading.attr="'lazy'"
      :customer-type.attr="userContext"
      :post-code.attr="postCode"
      @pdinitialize="handlePriceDisplayEcoFootnote"
    >
      <dh-price-display-additional-price-information
        class="emh-vehicle-tile__eco-bonus-label"
        @pdfootnotereferenceclick="scrollToFootnotes"
      >
      </dh-price-display-additional-price-information>
    </dh-price-display>
    <ul v-if="product.highlights && product.highlights.length" class="wb-vehicle-tile__highlight">
      <li v-for="(highlight, idx) in product.highlights" :key="idx" class="wb-vehicle-tile__highlight-item">
        <wb-tag :variant.attr="'tertiary'">
          <wb-icon class="wb-tag__icon wb-vehicle-tile__icon hydrated" aria-hidden="true" :name.attr="'checkmark'">
          </wb-icon>
          <span>{{ highlight }}</span>
        </wb-tag>
      </li>
    </ul>
    <emico-data
      v-if="isEmicoDataAvailable"
      class="wb-vehicle-tile__consumption"
      show-energy-efficiency-image
      :emico-data="product.emicoData"
      :emico-data-wltp="product.emicoDataWltp"
      :show-nefz-overlay="showNefzOverlay"
      :show-wltp-overlay="showWltpOverlay"
      :type="emicoDataType"
    ></emico-data>

    <dealer-display :product="product" :footnote-label="$t('dcp.srp.footnotes.dealerLocation')" />

    <compare-checkbox
      v-if="isCompareVisible"
      :name="product.name"
      :code="product.code"
      :commission-number="product.commissionNumber"
      :image="product.images[0].url"
    />

    <div class="wb-vehicle-tile__load-more-wrapper">
      <div class="wb-divider"></div>
      <emh-router-link
        class="wb-button wb-button--tertiary wb-button--large"
        :data-anchor="product.commissionNumber"
        :to="{
          name: 'pdp',
          params: {
            code: product.code,
          },
        }"
        @click.prevent="trackLinkClickEvent"
      >
        {{ $t('dcp.products.open') }}
      </emh-router-link>
    </div>
  </article>
</template>

<script lang="ts">
import CampaignTags from '@dh-io-emh/shared/src/components/campaignTags/index.vue';
import EmicoData from '@dh-io-emh/shared/src/components/emico/data/index.vue';
import FootnoteReference from '@dh-io-emh/shared/src/components/footnotes/footnoteReference.vue';
import { useFootnoteReference } from '@dh-io-emh/shared/src/composables/footnote-reference.composable';
import { scrollToFootnotesContainer } from '@dh-io-emh/shared/src/utils/footnote.util';
import stockStatus from '@dh-io-emh/shared/src/components/stockStatus/index.vue';
import EmhRouterLink from '@dh-io-emh/shared/src/components/utils/emhRouterLink/index.vue';
import FinanceSummary from '@dh-io-emh/shared/src/components/finance/FinanceSummary.vue';
import { usePowerFootnote, usePriceBadgeFootnote } from '@dh-io-emh/shared/src/composables/footnote.composable';
import { useI18n } from '@dh-io-emh/shared/src/composables/i18n.composable';
import { usePostCode } from '@dh-io-emh/shared/src/composables/post-code.composable';
import usePriceDisplay from '@dh-io-emh/shared/src/composables/price-display.composable';
import { useTracking } from '@dh-io-emh/shared/src/composables/tracking.composable';
import { useUserContext } from '@dh-io-emh/shared/src/composables/user-context.composable';
import { useSteeringWheelInformation } from '@dh-io-emh/shared/src/composables/steering-wheel.composable';
import { IntersectionObserverDirective } from '@dh-io-emh/shared/src/directives/intersectionObserver';
import { infuse } from '@dh-io-emh/shared/src/ioc-config';
import { getFuelTypeIcon } from '@dh-io-emh/shared/src/utils/fuel-type-icon.util';
import { BreakdownType, PriceDisplayEventData } from '@dh-io-pls/price-display';
import { computed, defineComponent, onMounted, PropType, ref, toRefs } from 'vue';
import { PriceType } from '@dh-io-emh/shared/src/types/price.type';
import { FuelTypeCode } from '@dh-io-emh/commerce-api/src/models/product';
import CompareCheckbox from '../compare/compare-checkbox/index.vue';
import Vehicle from '../../models/vehicle.model';
import vehicleNoImageUrl from '@dh-io-emh/shared/src/assets/images/vehicle_no_image.png';
import DealerDisplay from './dealer-display/index.vue';
import { EmicoType } from '@dh-io-emh/config-types';
import { StockType } from '@dh-io-emh/shared/src/models/vehicle.model';

export default defineComponent({
  name: 'ProductTile',
  directives: {
    intersectionObserver: IntersectionObserverDirective as any,
  },
  components: {
    CompareCheckbox,
    FootnoteReference,
    EmicoData,
    EmhRouterLink,
    CampaignTags,
    stockStatus,
    FinanceSummary,
    DealerDisplay,
  },
  props: {
    /**
     * Product information
     */
    product: {
      type: Object as PropType<Vehicle>,
      required: true,
    },
    /**
     * Index of current tile
     */
    index: {
      type: Number,
      required: true,
    },
    /**
     * Compare checkbox is available
     */
    isCompareVisible: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['popover-open', 'tag-expand', 'tag-modal-open'],

  setup(props, { emit }) {
    const {
      apiBaseUrl,
      srp: {
        showSummaryWidget,
        showWltpOverlay,
        showNefzOverlay,
        priceFirstPosition,
        showWishlistSaveButton,
        showSteeringWheelPosition,
      },
      footnotes: { showDealerLocationFootnote, showPriceFootnote, showImagesFootnote },
      showStrikePrice,
      campaignBadgesVersion,
    } = infuse('config');

    const { postCode } = usePostCode();

    const { product } = toRefs(props);
    const { t } = useI18n();
    const hasFinCalcError = ref(false);
    const hasImageLoadError = ref(false);
    const isInViewport = ref(false);
    const fuelTypeIcon = getFuelTypeIcon(props.product.fuelType?.code as FuelTypeCode);
    const priceDisplay = ref<(HTMLElement & Record<string, unknown>) | undefined>();
    const priceDisplayEco = ref<(HTMLElement & Record<string, unknown>) | undefined>();
    const productImages = product.value?.images[0];
    const hasValidImageUrl = computed(() => Boolean(productImages?.url));
    const { generateReference } = useFootnoteReference('productTile');
    const { handlePpdwcFootnotes: handlePriceDisplayEcoFootnote, totalPrice } = usePriceDisplay(
      priceDisplayEco,
      product,
      generateReference('priceDisplayEco'),
    );
    const { handlePpdwcFootnotes: handlePriceDisplayFootnote, addEmhFootnote } = usePriceDisplay(
      priceDisplay,
      product,
      generateReference('priceDisplay'),
    );
    const { steeringWheelPosition } = useSteeringWheelInformation(product.value.steeringWheel?.code);

    const vehicleImageUrl = computed(() => {
      if (!hasValidImageUrl.value || hasImageLoadError.value) {
        return vehicleNoImageUrl;
      }

      const urlString = productImages?.url;

      // Self-hosted images
      if (urlString[0] === '/') {
        // Append root api url to image path.
        return `${apiBaseUrl.replace('dcp-api', '')}${urlString}`;
      }

      // 'im', 'fuzz' params according to WB implementation documentation
      // 'padding' param seams to have no effect over and without im=Trim option
      // #MPC-8378: Resize product tile image to the max necessary size
      // WB template at https://dev-wb3.oneweb.mercedes-benz.com/developer/style-components/vehicle-tile#with-one-price
      const imageUrl = new URL(urlString);

      imageUrl.searchParams.set('im', 'Trim');
      imageUrl.searchParams.set('fuzz', '0.5');
      // Size is applied before trim. So 670px is an rough estimation
      // to output an image width of aprox. 390px
      imageUrl.searchParams.set('width', '670');

      return imageUrl.href;
    });

    const { trackLinkClick, getProductDataTrackingObject } = useTracking();

    const trackLinkClickEvent = async () => {
      trackLinkClick({
        eventDetail: {
          category: 'link',
          action: 'product_list',
          label: 'view_details',
          products: getProductDataTrackingObject({
            product: props.product,
            index: props.index,
            price: totalPrice.value,
          }),
        },
      });
    };

    const trackWishlistChanging = (label: string) => {
      const eventDetail = {
        category: 'feature',
        action: 'wishlist',
        label,
        products: getProductDataTrackingObject({
          product: props.product,
          index: props.index,
          price: totalPrice.value,
        }),
      };

      trackLinkClick({ eventDetail });
    };

    onMounted(async () => {
      if (showImagesFootnote) {
        addEmhFootnote('PURCHASE_PRICE' as BreakdownType, t('dcp.srp.footnotes.images'));
      }
    });

    const setFinCalcError = (hasError) => {
      hasFinCalcError.value = hasError;
    };

    const onImageLoadError = () => {
      hasImageLoadError.value = true;
    };

    const onPriceDisplayChange = (data: CustomEvent<PriceDisplayEventData>) => {
      handlePriceDisplayFootnote(data);
    };

    const scrollToFootnotes = () => {
      scrollToFootnotesContainer();
    };

    const displayReversedPrices = computed(() => priceFirstPosition === 'rate');

    const emicoDataType = ref<EmicoType>('wltp');
    const isEmicoDataAvailable = computed(() => {
      const { emicoData, emicoDataWltp } = props.product;
      const isNefzData = emicoData && emicoData.length;
      const isWltpData = emicoDataWltp && emicoDataWltp.length;

      return emicoDataType.value === 'wltp'
        ? isWltpData || (isNefzData && showNefzOverlay)
        : isNefzData || (isWltpData && showWltpOverlay);
    });

    const showPowerFootnote = usePowerFootnote(props.product);
    const showPriceBadgeFootnote = usePriceBadgeFootnote(props.product.badges);

    const showTransmission = computed(
      () =>
        product.value.transmission && product.value.gearBox && product.value?.fuelType?.code !== FuelTypeCode.ELECTRIC,
    );

    const onPopoverOpen = (type: PriceType) => emit('popover-open', type);
    const onTagExpand = () => emit('tag-expand');
    const onModalOpen = () => emit('tag-modal-open');

    const userContext = useUserContext();

    return {
      displayReversedPrices,
      fuelTypeIcon,
      hasFinCalcError,
      hasImageLoadError,
      hasValidImageUrl,
      isInViewport,
      setFinCalcError,
      onImageLoadError,
      onPopoverOpen,
      onPriceDisplayChange,
      onTagExpand,
      onModalOpen,
      priceDisplay,
      priceDisplayEco,
      scrollToFootnotes,
      showDealerLocationFootnote,
      showImagesFootnote,
      showNefzOverlay,
      showPowerFootnote,
      showPriceBadgeFootnote,
      showPriceFootnote,
      showSteeringWheelPosition,
      showWishlistSaveButton,
      showWltpOverlay,
      showTransmission,
      showStrikePrice,
      steeringWheelPosition,
      trackLinkClickEvent,
      trackWishlistChanging,
      handlePriceDisplayEcoFootnote,
      handlePriceDisplayFootnote,
      vehicleImageUrl,
      showSummaryWidget,
      userContext,
      postCode,
      isEmicoDataAvailable,
      emicoDataType,
      PriceType,
      generateReference,
      campaignBadgesVersion,
      StockType,
    };
  },
});
</script>
<!-- DO not use scoped. We need this styles to propagate to child components -->
<style src="./productTile.scss" lang="scss"></style>
